import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FiltersSidebar from "../components/FiltersSidebar";
import PropertyCard from "../components/PropertyCard";
import MapView from "../components/MapView";
import { api } from "../lib/api";
import { useLang } from "../context/LangContext";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { Map as MapIcon, List as ListIcon, SlidersHorizontal } from "lucide-react";

export default function Results() {
  const { t } = useLang();
  const [sp, setSp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const PAGE_SIZE = 12;

  const filters = useMemo(() => Object.fromEntries(sp.entries()), [sp]);

  useEffect(() => {
    setLoading(true); setPage(1);
    const params = { page: 1, page_size: PAGE_SIZE };
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
    if (!params.transaction) params.transaction = "sale";
    api.get("/properties", { params }).then((r) => {
      setItems(r.data.items); setTotal(r.data.total);
      setHasMore(r.data.items.length < r.data.total);
    }).finally(() => setLoading(false));
  }, [filters]);

  const loadMore = async () => {
    const next = page + 1;
    const params = { page: next, page_size: PAGE_SIZE };
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
    if (!params.transaction) params.transaction = "sale";
    const r = await api.get("/properties", { params });
    setItems((arr) => [...arr, ...r.data.items]);
    setPage(next);
    setHasMore(items.length + r.data.items.length < r.data.total);
  };

  const updateFilters = (next) => {
    const out = new URLSearchParams();
    Object.entries(next).forEach(([k, v]) => { if (v && v !== "all" && v !== "") out.set(k, v); });
    setSp(out);
  };
  const reset = () => setSp(new URLSearchParams({ transaction: "sale" }));

  return (
    <div className="min-h-screen">
      <Header />
      <div className="pt-24 max-w-7xl mx-auto px-5 md:px-10 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#111827]">
              {filters.transaction === "rent" ? t("forRent") : t("forSale")}
              {filters.wilaya && ` · ${filters.wilaya}`}
              {filters.city && ` · ${filters.city}`}
            </h1>
            <p className="text-slate-500 mt-1 text-sm" data-testid="results-count">{total} {total > 1 ? t("results") : t("result")}</p>
          </div>
          <div className="flex gap-2">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" className="lg:hidden" data-testid="mobile-filters-trigger"><SlidersHorizontal size={15} className="mr-2" />{t("filters")}</Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="h-[85vh] overflow-auto rounded-t-2xl">
                <FiltersSidebar filters={filters} onChange={updateFilters} onReset={reset} />
              </SheetContent>
            </Sheet>
            <div className="flex rounded-xl bg-slate-100 p-1">
              <button onClick={() => setView("list")} className={`px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-1.5 ${view === "list" ? "bg-white text-[#1B8F5A] shadow-sm" : "text-slate-500"}`} data-testid="view-list"><ListIcon size={14} />{t("listView")}</button>
              <button onClick={() => setView("map")} className={`px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-1.5 ${view === "map" ? "bg-white text-[#1B8F5A] shadow-sm" : "text-slate-500"}`} data-testid="view-map"><MapIcon size={14} />{t("mapView")}</button>
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          <div className="hidden lg:block"><FiltersSidebar filters={filters} onChange={updateFilters} onReset={reset} /></div>
          <div className="flex-1 min-w-0">
            {view === "map" ? (
              <MapView items={items} height={620} />
            ) : loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">{[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden border border-slate-200 animate-pulse"><div className="aspect-[4/3] bg-slate-100" /><div className="p-5 space-y-3"><div className="h-5 bg-slate-100 rounded w-2/3" /><div className="h-4 bg-slate-100 rounded w-1/2" /></div></div>
              ))}</div>
            ) : items.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center"><p className="text-slate-500">{t("noResults")}</p></div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">{items.map((p) => <PropertyCard key={p.id} item={p} />)}</div>
                {hasMore && (
                  <div className="mt-10 text-center">
                    <Button onClick={loadMore} variant="outline" className="px-8 py-6 text-base" data-testid="load-more">{t("viewMore")}</Button>
                  </div>
                )}
                {!hasMore && items.length > 0 && (
                  <div className="mt-10 text-center text-sm text-slate-400">{t("endOfResults")}</div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
